/** Active alerts for a home: leaks, alarm, offline, unavailable critical things. */
import type { AlertItem, HomeModel, ModuleId } from '@home/shared';
import { useAccess, useDerived, useHomeSummary, useModel, useServerAlerts } from './hooks.js';
import { store } from './store.js';
import { formatWhen, relativeTime } from './format.js';

export interface Alert {
  id: string;
  tone: 'alert' | 'caution' | 'info';
  icon: string;
  title: string;
  detail?: string;
  entityId?: string;
  roomId?: string | null;
  /** Dashboard this alert belongs to (server alerts carry it; client-derived ones set it here). */
  module?: ModuleId;
  /** Server alerts: epoch s the condition started. */
  since?: number;
  /** Grouped server alerts (devices offline): what's behind it. */
  items?: AlertItem[];
}

const DANGER_DC = new Set(['moisture', 'smoke', 'gas', 'carbon_monoxide', 'safety']);

const watchCache = new WeakMap<HomeModel, string[]>();
export function alertWatchIds(model: HomeModel | undefined): string[] {
  if (!model) return [];
  const hit = watchCache.get(model);
  if (hit) return hit;
  const climate = new Set(model.climate.flatMap((c) => c.sources.map((s) => s.entityId)));
  const ids = Object.values(model.entities)
    .filter(
      (m) =>
        m.category === null &&
        (m.domain === 'alarm_control_panel' ||
          m.domain === 'lock' ||
          (m.domain === 'binary_sensor' && m.deviceClass !== null && DANGER_DC.has(m.deviceClass)) ||
          climate.has(m.id)),
    )
    .map((m) => m.id);
  watchCache.set(model, ids);
  return ids;
}

export function useAlerts(home: string): Alert[] {
  const model = useModel(home);
  const access = useAccess(home);
  const summary = useHomeSummary(home);
  const ids = alertWatchIds(model);
  const derived = useDerived(home, ids, () => {
    const out: Alert[] = [];
    if (!model) return out;
    const unavailable: string[] = [];
    for (const id of ids) {
      const m = model.entities[id];
      const s = store.entity(home, id);
      if (!m) continue;
      if (!s || s.s === 'unavailable') {
        if (m.domain !== 'binary_sensor') unavailable.push(m.name);
        continue;
      }
      if (m.domain === 'binary_sensor' && s.s === 'on') {
        const leak = m.deviceClass === 'moisture';
        out.push({
          id: `danger:${id}`,
          tone: 'alert',
          icon: leak ? 'leak' : 'smoke',
          module: leak ? 'leak' : 'security',
          title: leak ? `Leak: ${m.name}` : `${m.name}`,
          detail: `Detected ${relativeTime(s.lc)}`,
          entityId: id,
          roomId: m.roomId,
        });
      }
      if (m.domain === 'alarm_control_panel') {
        if (s.s === 'triggered')
          out.push({ id: `alarm:${id}`, tone: 'alert', icon: 'siren', title: `${m.name} triggered`, entityId: id, roomId: m.roomId, module: 'security' });
        else if (s.s === 'pending' || s.s === 'arming')
          out.push({ id: `alarm:${id}`, tone: 'caution', icon: 'shield', title: `${m.name}: ${s.s === 'pending' ? 'entry delay' : 'arming'}`, entityId: id, roomId: m.roomId, module: 'security' });
      }
      if (m.domain === 'lock' && s.s === 'jammed')
        out.push({ id: `lock:${id}`, tone: 'alert', icon: 'lock', title: `${m.name} is jammed`, entityId: id, roomId: m.roomId, module: 'door' });
    }
    if (unavailable.length)
      out.push({
        id: 'unavailable',
        tone: 'caution',
        icon: 'wifi-off',
        title: unavailable.length === 1 ? `${unavailable[0]} is unavailable` : `${unavailable.length} devices are unavailable`,
        detail: unavailable.slice(0, 4).join(', ') + (unavailable.length > 4 ? '…' : ''),
      });
    return out;
  });

  const server = useServerAlerts(home);

  const head: Alert[] = [];
  if (summary && !summary.online)
    head.push({ id: 'offline', tone: 'alert', icon: 'wifi-off', title: `${summary.name} is offline`, detail: 'Showing the last known state' });
  if (access && !access.inWindow)
    head.push({
      id: 'window',
      tone: 'info',
      icon: 'clock',
      title: access.nextChangeAt ? `Controls return ${formatWhen(access.nextChangeAt, model?.home.timezone)}` : 'Controls are paused',
      detail: 'You can see everything, but changes are off outside your access hours.',
    });
  // Server rules (pH/ORP bands, out-of-sync thermostats, cell imbalance…). Skip
  // what the client already derives: offline, and anything about the same entity.
  const tail: Alert[] = [];
  if (server.length) {
    const seen = new Set(derived.map((d) => d.entityId).filter(Boolean));
    for (const a of server) {
      if (a.id === 'offline' || a.id.startsWith('offline:')) continue;
      if (a.entityId && seen.has(a.entityId)) continue;
      if (a.id.startsWith('critical-unavailable') && derived.some((d) => d.id === 'unavailable')) continue;
      tail.push({
        id: `srv:${a.id}`,
        tone: a.severity === 'critical' ? 'alert' : a.severity === 'warning' ? 'caution' : 'info',
        icon: a.severity === 'info' ? 'info' : 'alert',
        title: a.title,
        detail: a.detail,
        since: a.since,
        ...(a.module ? { module: a.module } : {}),
        ...(a.items?.length ? { items: a.items } : {}),
        ...(a.entityId ? { entityId: a.entityId, roomId: model?.entities[a.entityId]?.roomId ?? null } : {}),
      });
    }
  }
  return head.length || tail.length ? [...head, ...derived, ...tail] : derived;
}
